// src/components/connexion/EcranConnexion.tsx — SCE Issues
// Page de connexion V2 (Lot 11) et écran d'ouverture de session.
//
// Maquette validée le 25/09 (piste A « le parcours ») :
// https://claude.ai/artifact/QfvSN4oMSkjimiQV9GEwud
//
// Pourquoi une page moins vide : c'est le premier écran de tout nouvel
// utilisateur (Phase 3 : tous les services SCE). Au bureau, la colonne de
// droite montre en un coup d'œil ce que fait l'outil, de la déclaration à la
// clôture. Sous 1100 px, la même histoire en liste, bouton en bas, à portée
// de pouce.
//
// Règles de la charte tenues ici : le jaune ne sert QU'AU bouton (l'ancienne
// page était entièrement jaune) ; les exemples utilisent les vrais badges de
// l'application, donc les couleurs métier réglables (Lot 8, posées avant le
// premier rendu par main.tsx). Animations coupées si le poste demande moins
// d'animations (globals.css).
//
// Piste B (« la machine ») : gardée pour une alternance plus tard.
//
// Retours de recette du 25/09 : la page tient dans l'écran (colonne de droite
// mise à l'échelle de la place disponible) ; l'écran d'ouverture dure un peu,
// sa barre se remplit pendant que la page d'arrivée charge ses listes, puis
// s'efface dans un fondu (flou essayé puis retiré : trop appuyé).

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import {
  AlertCircle,
  ArrowRight,
  Bell,
  Camera,
  CheckCircle2,
  FileText,
  Lock,
  Mail,
} from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { LogoSartorius } from '@/components/ui/Marque'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import type { MotifConnexion } from '@/auth/session'

/** Qui contacter quand la connexion bloque (texte de la maquette). */
const CONTACT_CONNEXION = 'Guillaume Delavault'

/** Au-delà : deux colonnes. En dessous : une colonne (téléphone, tablette). */
const LARGE = '(min-width: 1100px)'

interface EcranConnexionProps {
  /** Pourquoi on est là : après une déconnexion, ou une connexion ratée. */
  motif: MotifConnexion | null
  onConnecter: () => void
}

export function EcranConnexion({ motif, onConnecter }: EcranConnexionProps): JSX.Element {
  const large = useMediaQuery(LARGE)
  const libelleBouton = motif === 'echec'
    ? 'Réessayer'
    : large ? 'Se connecter avec mon compte Sartorius' : 'Se connecter'

  const bouton = (
    <button type="button" className="connexion-bouton" onClick={onConnecter}>
      {libelleBouton}
      <ArrowRight size={20} strokeWidth={2} aria-hidden="true" />
    </button>
  )

  if (!large) {
    return (
      <main
        className="flex flex-col justify-between"
        style={{
          minHeight: '100dvh',
          boxSizing: 'border-box',
          padding: '40px 20px calc(28px + env(safe-area-inset-bottom, 0px))',
          gap: 32,
          background: 'var(--color-white)',
        }}
      >
        <div className="flex flex-col" style={{ gap: 36 }}>
          <div className="flex flex-col" style={{ gap: 12 }}>
            <Surtitre hauteurLogo={15} taille={11} />
            <h1 style={{ margin: 0, fontSize: 44, lineHeight: '48px', fontWeight: 300 }}>SCE Issues</h1>
            <p style={{ margin: 0, fontSize: 17, lineHeight: '24px', color: 'var(--color-gray-90)' }}>
              Un problème sur un équipement&nbsp;? Déclarez-le en moins d’une minute.
            </p>
          </div>
          <EtapesListe />
        </div>

        <div className="flex flex-col" style={{ gap: 14 }}>
          {motif && <Message motif={motif} />}
          {bouton}
          <Rassurance texte="Compte Sartorius · session gardée sur cet appareil" centre />
        </div>
      </main>
    )
  }

  return (
    <main className="flex" style={{ height: '100dvh', minHeight: 600, background: 'var(--color-white)' }}>
      <div
        className="flex flex-col justify-between"
        style={{ width: 600, flexShrink: 0, boxSizing: 'border-box', padding: 'clamp(32px, 7vh, 64px) 80px', gap: 32 }}
      >
        <Surtitre hauteurLogo={20} taille={12} />

        <div className="flex flex-col" style={{ gap: 28 }}>
          <div className="flex flex-col" style={{ gap: 16 }}>
            <h1 style={{ margin: 0, fontSize: 64, lineHeight: '68px', fontWeight: 300 }}>SCE Issues</h1>
            <p style={{ margin: 0, fontSize: 21, lineHeight: '30px', color: 'var(--color-gray-90)', maxWidth: 420 }}>
              Un problème sur un équipement&nbsp;? Déclarez-le en moins d’une minute&nbsp;: le bon coordinateur est prévenu.
            </p>
          </div>
          <div className="flex flex-col" style={{ gap: 14, width: 420 }}>
            {motif && <Message motif={motif} />}
            {bouton}
            <Rassurance texte="Votre session reste ouverte sur cet appareil." />
          </div>
        </div>

        <div style={{ fontSize: 14, lineHeight: '20px', color: 'var(--color-text-secondary)' }}>
          Un souci pour vous connecter&nbsp;?<br />{CONTACT_CONNEXION}
        </div>
      </div>

      <PanneauEtapes />
    </main>
  )
}

// ─── Écran d'ouverture de session ───

/** Assez long pour être vu, et pour que la page d'arrivée charge derrière. */
const DUREE_MIN_MS = 1000
/** Barre qui finit de se remplir, puis fondu. */
const DUREE_REMPLISSAGE_MS = 400
const DUREE_FONDU_MS = 500

type PhaseOuverture = 'depart' | 'monte' | 'complete' | 'fondu'

/**
 * Voile blanc posé AU-DESSUS de l'application dès le chargement, qui remplace
 * l'ancien écran noir. Depuis la session gardée (Lot 10), c'est l'écran le
 * plus vu.
 *
 * La page d'arrivée (accueil, ou la fiche d'un lien de mail) se monte DESSOUS
 * pendant ce temps et charge ses listes. La barre monte jusqu'à 85 % ; quand
 * `pret` passe à vrai (App.tsx : réseau calme, ou écran de connexion) et que
 * la durée minimale est écoulée, elle se complète, puis le voile s'efface en
 * fondu et laisse la page déjà remplie.
 */
export function OuvertureSession({ pret, onTermine }: { pret: boolean; onTermine: () => void }): JSX.Element {
  const [phase, setPhase] = useState<PhaseOuverture>('depart')
  const debut = useRef(performance.now())

  // 0 → 85 % : lancé à l'image suivante, pour que la transition parte de 0.
  useEffect(() => {
    const id = requestAnimationFrame(() => setPhase('monte'))
    return () => cancelAnimationFrame(id)
  }, [])

  useEffect(() => {
    if (phase === 'monte' && pret) {
      const reste = Math.max(0, DUREE_MIN_MS - (performance.now() - debut.current))
      const t = setTimeout(() => setPhase('complete'), reste)
      return () => clearTimeout(t)
    }
    if (phase === 'complete') {
      const t = setTimeout(() => setPhase('fondu'), DUREE_REMPLISSAGE_MS)
      return () => clearTimeout(t)
    }
    if (phase === 'fondu') {
      // Un minuteur plutôt qu'`animationend` : sans animation (poste réglé
      // pour en avoir moins), l'événement ne viendrait jamais.
      const reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const t = setTimeout(onTermine, reduit ? 0 : DUREE_FONDU_MS)
      return () => clearTimeout(t)
    }
    return undefined
  }, [phase, pret, onTermine])

  const largeur = phase === 'depart' ? '0%' : phase === 'monte' ? '85%' : '100%'
  const transition = phase === 'monte'
    ? 'width 2.4s cubic-bezier(0.1, 0.6, 0.3, 1)'
    : `width ${DUREE_REMPLISSAGE_MS}ms ease-out`

  return (
    <div className={`ouverture${phase === 'fondu' ? ' ouverture-fondu' : ''}`} role="status">
      <div className="ouverture-contenu flex flex-col items-center" style={{ gap: 22 }}>
        <div style={{ fontSize: 36, fontWeight: 300 }}>SCE Issues</div>
        <div className="connexion-barre" aria-hidden="true">
          <div className="connexion-barre-remplissage" style={{ width: largeur, transition }} />
        </div>
        <div className="connexion-apparait" style={{ fontSize: 15, color: 'var(--color-text-secondary)' }}>
          Ouverture de votre session…
        </div>
      </div>
      {/* La marque signe en pied, comme au lancement d'une appli ; le nom de
          l'outil garde le centre (maquette du 29/09). */}
      <div
        className="ouverture-contenu"
        style={{ position: 'absolute', left: 0, right: 0, bottom: 'calc(34px + env(safe-area-inset-bottom, 0px))', display: 'flex', justifyContent: 'center' }}
      >
        <LogoSartorius hauteur={28} />
      </div>
    </div>
  )
}

// ─── Morceaux communs ───

/** Logo Sartorius, filet, puis le site (maquette du 29/09 : remplace le
 *  surtitre « Sartorius · SCE Pompey » écrit en capitales). */
function Surtitre({ hauteurLogo, taille }: { hauteurLogo: number; taille: number }): JSX.Element {
  return (
    <div className="flex items-center" style={{ gap: 14 }}>
      <LogoSartorius hauteur={hauteurLogo} />
      <span aria-hidden="true" style={{ width: 1, alignSelf: 'stretch', backgroundColor: 'var(--color-black)', opacity: 0.35 }} />
      <span style={{ fontSize: taille, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
        SCE Pompey
      </span>
    </div>
  )
}

function Rassurance({ texte, centre = false }: { texte: string; centre?: boolean }): JSX.Element {
  return (
    <div
      className="flex items-center"
      style={{
        gap: 8,
        fontSize: 14,
        color: 'var(--color-text-secondary)',
        justifyContent: centre ? 'center' : 'flex-start',
      }}
    >
      <Lock size={16} strokeWidth={2} aria-hidden="true" style={{ flexShrink: 0 }} />
      {texte}
    </div>
  )
}

/** États « déconnecté » et « connexion ratée » (maquette : artboard États). */
function Message({ motif }: { motif: MotifConnexion }): JSX.Element {
  const echec = motif === 'echec'
  return (
    <div
      className="flex items-start"
      role={echec ? 'alert' : 'status'}
      style={{ gap: 12, background: 'var(--color-gray-5)', borderRadius: 12, padding: '16px 18px' }}
    >
      {echec
        ? <AlertCircle size={22} strokeWidth={2} aria-hidden="true" style={{ flexShrink: 0, color: 'var(--color-traffic-red)' }} />
        : <CheckCircle2 size={22} strokeWidth={2} aria-hidden="true" style={{ flexShrink: 0 }} />}
      <div className="flex flex-col" style={{ gap: 4 }}>
        <div style={{ fontSize: 16, fontWeight: 500 }}>
          {echec ? 'La connexion n’a pas abouti.' : 'Vous êtes déconnecté.'}
        </div>
        <div style={{ fontSize: 14, lineHeight: '20px', color: 'var(--color-gray-90)' }}>
          {echec
            ? `Réessayez. Si cela persiste : ${CONTACT_CONNEXION}`
            : 'La session ne se rouvrira plus toute seule sur cet appareil.'}
        </div>
      </div>
    </div>
  )
}

// ─── Le parcours : 4 étapes ───

const ETAPES_LISTE: { titre: string; detail: string }[] = [
  { titre: 'Déclarer', detail: 'N° de série, photo, description' },
  { titre: 'Qualifier', detail: 'Le coordinateur du métier est prévenu' },
  { titre: 'Traiter', detail: 'Échanges, photos et documents dans la fiche' },
  { titre: 'Clôturer', detail: 'Commentaire, badges qualité, tout reste tracé' },
]

/** Version une colonne : une liste reliée par un trait. */
function EtapesListe(): JSX.Element {
  return (
    <div className="relative">
      <div
        aria-hidden="true"
        style={{ position: 'absolute', left: 15, top: 20, bottom: 20, width: 2, background: 'var(--color-gray-20)' }}
      />
      <ol className="flex flex-col" style={{ gap: 22, margin: 0, padding: 0, listStyle: 'none' }}>
        {ETAPES_LISTE.map((e, i) => (
          <li
            key={e.titre}
            className="relative flex items-start connexion-entree"
            style={{ gap: 16, animationDelay: `${0.1 + i * 0.2}s` }}
          >
            <span
              className="flex items-center justify-center"
              style={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: 16,
                boxSizing: 'border-box',
                fontSize: 14,
                fontWeight: 500,
                background: i === 0 ? 'var(--color-black)' : 'var(--color-white)',
                color: i === 0 ? 'var(--color-white)' : 'var(--color-black)',
                border: i === 0 ? 'none' : '2px solid var(--color-black)',
              }}
            >
              {i + 1}
            </span>
            <span className="flex flex-col" style={{ gap: 2, paddingTop: 4 }}>
              <span style={{ fontSize: 17, fontWeight: 500 }}>{e.titre}</span>
              <span style={{ fontSize: 15, lineHeight: '20px', color: 'var(--color-text-secondary)' }}>{e.detail}</span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  )
}

/** Marge autour des étapes, et agrandissement maximal sur un très grand écran. */
const MARGE_PANNEAU = 48
const ECHELLE_MAX = 1.35

/**
 * Colonne de droite : les cartes gardent leur dessin (hauteurs fixes dont
 * dépend le repère animé) et sont mises à L'ÉCHELLE de la place disponible —
 * réduites sur un portable, agrandies sur un grand écran. La page tient ainsi
 * dans l'écran, sans défilement.
 */
function PanneauEtapes(): JSX.Element {
  const panneau = useRef<HTMLDivElement>(null)
  const contenu = useRef<HTMLDivElement>(null)
  const [echelle, setEchelle] = useState(1)

  useLayoutEffect(() => {
    const p = panneau.current
    const c = contenu.current
    if (!p || !c) return undefined
    // offsetWidth / offsetHeight ignorent la transformation : taille d'origine.
    const ajuster = (): void => {
      const k = Math.min(
        ECHELLE_MAX,
        (p.clientWidth - 2 * MARGE_PANNEAU) / c.offsetWidth,
        (p.clientHeight - 2 * MARGE_PANNEAU) / c.offsetHeight,
      )
      setEchelle(Math.max(0.5, k))
    }
    ajuster()
    const observateur = new ResizeObserver(ajuster)
    observateur.observe(p)
    return () => observateur.disconnect()
  }, [])

  return (
    <div
      ref={panneau}
      className="flex items-center justify-center"
      style={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', background: 'var(--color-gray-5)' }}
    >
      <div ref={contenu} style={{ flexShrink: 0, transform: `scale(${echelle})` }}>
        <EtapesCartes />
      </div>
    </div>
  )
}

/**
 * Version bureau : une carte par étape, avec un exemple. Hauteur FIXE des
 * cartes (156 px, écart 16) : le repère animé s'arrête au centre de chacune
 * (keyframes `connexion-parcours` de globals.css — à ajuster ensemble).
 */
function EtapesCartes(): JSX.Element {
  const cartes: { titre: string; sousTitre: string; contenu: ReactNode }[] = [
    {
      titre: 'Déclarer',
      sousTitre: 'N° de série, photo, description',
      contenu: (
        <div className="flex" style={{ gap: 12 }}>
          <div
            className="flex flex-col"
            style={{ flexGrow: 1, border: '1px solid var(--color-gray-20)', borderRadius: 10, padding: '8px 12px', gap: 2 }}
          >
            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Équipement</span>
            <span style={{ fontSize: 17 }}>
              5201-H<span className="connexion-curseur" aria-hidden="true" />
            </span>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Flowdrive · OF-2601-0025 · PN4334</span>
          </div>
          <div
            className="flex items-center justify-center"
            style={{ width: 84, borderRadius: 10, background: 'var(--color-gray-10)', color: 'var(--color-gray-80)' }}
          >
            <Camera size={26} strokeWidth={1.8} aria-hidden="true" />
          </div>
        </div>
      ),
    },
    {
      titre: 'Qualifier',
      sousTitre: 'le coordinateur est prévenu',
      contenu: (
        <div className="flex flex-col" style={{ gap: 10 }}>
          <span className="flex items-center" style={{ gap: 8, fontSize: 15, color: 'var(--color-gray-90)' }}>
            <Mail size={18} strokeWidth={1.8} aria-hidden="true" />
            Mail ou Teams, selon son choix
          </span>
          <span className="flex items-center" style={{ gap: 8 }}>
            <Badge metier="ELEC" />
            <Pastille>Repérage</Pastille>
            <Badge criticite="Critique" />
          </span>
        </div>
      ),
    },
    {
      titre: 'Traiter',
      sousTitre: 'échanges et suivi dans la fiche',
      contenu: (
        <div className="flex flex-col" style={{ gap: 12 }}>
          <span className="flex items-center" style={{ gap: 10 }}>
            <span
              className="flex items-center justify-center"
              style={{ width: 30, height: 30, flexShrink: 0, borderRadius: 15, background: 'var(--color-gray-10)', fontSize: 12, fontWeight: 500 }}
            >
              TH
            </span>
            <span style={{ fontSize: 15, color: 'var(--color-gray-90)' }}>« Fil inversé au bornier X2, reprise mardi. »</span>
          </span>
          <span className="flex" style={{ gap: 8 }}>
            <Pastille icone={<Camera size={15} strokeWidth={1.8} aria-hidden="true" />}>2 photos</Pastille>
            <Pastille icone={<FileText size={15} strokeWidth={1.8} aria-hidden="true" />}>EDE p. 12</Pastille>
            <Pastille icone={<Bell size={15} strokeWidth={1.8} aria-hidden="true" />}>3 abonnés</Pastille>
          </span>
        </div>
      ),
    },
    {
      titre: 'Clôturer',
      sousTitre: 'tout reste tracé',
      contenu: (
        <div className="flex flex-col" style={{ gap: 10 }}>
          <span className="flex items-center" style={{ gap: 8 }}>
            <Badge statut="Clos" />
            <Badge badgeCloture="FNC-T" libelle="FNC-T_2026-55" />
          </span>
          <span style={{ fontSize: 15, color: 'var(--color-gray-90)' }}>« Joint remplacé, essai d’étanchéité conforme. »</span>
        </div>
      ),
    },
  ]

  return (
    <section className="flex flex-col" style={{ width: 600, gap: 20 }} aria-label="De la déclaration à la clôture">
      <div style={{ fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--color-text-secondary)', paddingLeft: 56 }}>
        De la déclaration à la clôture
      </div>
      <div className="relative" style={{ paddingLeft: 56 }}>
        <div aria-hidden="true" style={{ position: 'absolute', left: 19, top: 78, bottom: 78, width: 2, background: 'var(--color-gray-20)' }} />
        <div aria-hidden="true" className="connexion-repere" />
        <ol className="flex flex-col" style={{ gap: 16, margin: 0, padding: 0, listStyle: 'none' }}>
          {cartes.map((c, i) => (
            <li
              key={c.titre}
              className="flex flex-col connexion-entree"
              style={{
                height: 156,
                boxSizing: 'border-box',
                background: 'var(--color-white)',
                borderRadius: 16,
                padding: '20px 24px',
                gap: 12,
                animationDelay: `${0.1 + i * 0.2}s`,
              }}
            >
              <div className="flex items-baseline" style={{ gap: 12 }}>
                <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>{i + 1}</span>
                <span style={{ fontSize: 21, fontWeight: 500 }}>{c.titre}</span>
                <span style={{ fontSize: 15, color: 'var(--color-text-secondary)' }}>{c.sousTitre}</span>
              </div>
              {c.contenu}
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

/** Pastille neutre (famille, pièces jointes…) : contour gris, pas de couleur. */
function Pastille({ children, icone }: { children: ReactNode; icone?: ReactNode }): JSX.Element {
  return (
    <span
      className="inline-flex items-center"
      style={{ gap: 6, fontSize: 14, padding: '4px 12px', borderRadius: 999, border: '1px solid var(--color-gray-20)' }}
    >
      {icone}
      {children}
    </span>
  )
}
